// src/features/admin/components/settings/integrations/components/LegalResearchTab.tsx
import React from 'react';
import {
  MagnifyingGlassIcon,
  ClockIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';

const RESEARCH_PROVIDERS = [
  {
    name: 'LexisNexis',
    description: 'Comprehensive legal research with Shepard\'s Citations and extensive secondary sources.',
    content: 'Case law, statutes, regulations, secondary sources',
    features: ['Shepard\'s Citations', 'Practice area focused', 'News & business', 'AI-powered search']
  },
  {
    name: 'Westlaw',
    description: 'Industry-leading legal research with KeyCite and West headnotes.',
    content: 'Case law, statutes, regulations, secondary sources',
    features: ['KeyCite', 'West headnotes', 'Practice areas', 'Practical Law']
  },
  {
    name: 'Fastcase',
    description: 'Cost-effective legal research included with many bar associations.',
    content: 'Case law, statutes, regulations',
    features: ['Bar association access', 'AI search', 'Citator', 'Analytics']
  },
  {
    name: 'Casetext',
    description: 'AI-powered legal research with CARA brief analysis.',
    content: 'Case law, statutes, CARA AI',
    features: ['CARA AI', 'Brief analysis', 'Parallel search', 'SmartCite']
  },
  {
    name: 'Bloomberg Law',
    description: 'Corporate and regulatory focused research with analytics.',
    content: 'Corporate, regulatory, dockets',
    features: ['Docket analytics', 'Corporate deals', 'Regulatory', 'Practical guidance']
  },
  {
    name: 'Practical Law',
    description: 'Practice guides, templates, and how-to resources for transactional work.',
    content: 'Practice notes, templates, checklists',
    features: ['Practice notes', 'Standard documents', 'Checklists', 'What\'s Market']
  }
];

const LegalResearchTab: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Coming Soon Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <ClockIcon className="h-12 w-12 text-amber-600 dark:text-amber-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
          Legal Research Integration - Coming in Phase 2
        </h3>
        <p className="text-sm text-amber-700 dark:text-amber-300 max-w-2xl mx-auto">
          Search legal databases directly from EdgeLex, import citations, and link research to matters.
          This feature is scheduled for Phase 2 of development (Weeks 11-18).
        </p>
      </div>

      {/* Preview */}
      <div>
        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <BookOpenIcon className="h-5 w-5 mr-2 text-lex-accent" />
          Planned Research Platform Integrations
        </h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {RESEARCH_PROVIDERS.map((provider, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 opacity-75"
            >
              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                {provider.name}
              </h5>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                {provider.description}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                <strong>Content:</strong> {provider.content}
              </p>
              <div className="flex flex-wrap gap-1">
                {provider.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Integration Benefits */}
      <div className="bg-lex-accent-soft dark:bg-lex-accent-soft border border-lex-accent dark:border-lex-accent rounded-lg p-4">
        <h5 className="text-sm font-semibold text-lex-accent dark:text-lex-accent mb-2">
          Integration Benefits
        </h5>
        <ul className="text-xs text-lex-accent dark:text-lex-accent space-y-1">
          <li>• <strong>Unified Search:</strong> Search across multiple platforms from EdgeLex</li>
          <li>• <strong>Citation Import:</strong> Import citations directly into documents</li>
          <li>• <strong>Matter Linking:</strong> Associate research with specific matters</li>
          <li>• <strong>AI Enhancement:</strong> Lex can cite relevant cases in drafts</li>
        </ul>
      </div>
    </div>
  );
};

export default LegalResearchTab;
